import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import {
  ClipboardDocumentCheckIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  PencilIcon,
  XMarkIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PhotoIcon,
  CurrencyDollarIcon,
  QrCodeIcon
} from '@heroicons/react/24/outline';
import QRCode from 'qrcode';
import WhatsAppIcon from '../components/WhatsAppIcon';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import { getAppSocket } from '../lib/wesSocket';
import { Button } from '../components/ui/button';
import { ButtonGroup } from '../components/ui/button-group';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ClientCombobox from '../components/ClientCombobox';
import VehicleCombobox from '../components/VehicleCombobox';
import VehicleCreateDialog from '../components/VehicleCreateDialog';
import { openPdf } from '../utils/pdfHelpers';
import { confirmDialog } from '../components/ConfirmDialog';
import { Tip } from '../components/ui/tip';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../contexts/AuthContext';

interface VehicleDiagnosis {
  id: string;
  diagnosisNumber: string;
  clientId: string;
  clientName: string;
  clientEmail?: string;
  clientPhone?: string;
  vehicleId?: string;
  vehiclePlate?: string;
  vehicleBrand?: string;
  vehicleModel?: string;
  vehicleYear?: string;
  status: 'open' | 'delivered' | 'budgeted' | 'approved' | 'closed' | 'not_converted';
  urgency?: 'baja' | 'media' | 'alta' | 'critica';
  mileage?: number;
  observations?: string;
  relatedBudgetId?: string;
  emailSent?: number;
  whatsappSent?: number;
  statusChangedAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface DiagnosisItem {
  id?: string;
  category: string;
  subcategory?: string;
  description: string;
  severity: 'baja' | 'media' | 'alta' | 'critica';
  recommendation?: string;
  images?: string[];
}

interface Client {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  vehicles?: Vehicle[];
}

interface Vehicle {
  id: string;
  plate: string;
  brand: string;
  model: string;
  year: string;
  mileage?: number;
}

const CATEGORIES = [
  'Motor',
  'Frenos',
  'Suspension',
  'Neumaticos',
  'Transmision',
  'Sistema Electrico',
  'Carroceria',
  'Direccion',
  'Escape',
  'Refrigeracion',
  'Otros'
];

const SEVERITIES = [
  { value: 'baja', label: 'Baja', color: 'green' },
  { value: 'media', label: 'Media', color: 'yellow' },
  { value: 'alta', label: 'Alta', color: 'orange' },
  { value: 'critica', label: 'Critica', color: 'red' }
];

const SUBCATEGORIES: Record<string, string[]> = {
  Motor: ['Bujias', 'Filtro de Aire', 'Filtro de Aceite', 'Correa de Distribucion', 'Aceite', 'Radiador', 'Bateria', 'Alternador', 'Motor de Arranque', 'Bomba de Agua', 'Termostato', 'Sensores', 'Otros'],
  Frenos: ['Pastillas Delanteras', 'Pastillas Traseras', 'Discos Delanteros', 'Discos Traseros', 'Liquido de Frenos', 'Cables de Freno', 'Cilindro Maestro', 'Bombas de Freno', 'Freno de Mano', 'ABS', 'Otros'],
  Suspension: ['Amortiguadores Delanteros', 'Amortiguadores Traseros', 'Espirales', 'Bujes', 'Rotulas', 'Bieletas', 'Barras Estabilizadoras', 'Cazoletas', 'Otros'],
  Neumaticos: ['Neumatico Delantero Izquierdo', 'Neumatico Delantero Derecho', 'Neumatico Trasero Izquierdo', 'Neumatico Trasero Derecho', 'Neumatico de Auxilio', 'Valvulas', 'Balanceo', 'Alineacion', 'Rotacion', 'Otros'],
  Transmision: ['Embrague', 'Caja de Cambios', 'Aceite de Transmision', 'Palanca de Cambios', 'Cables de Transmision', 'Semieje', 'Diferencial', 'Otros'],
  'Sistema Electrico': ['Bateria', 'Alternador', 'Motor de Arranque', 'Luces Delanteras', 'Luces Traseras', 'Luces de Freno', 'Luces Direccionales', 'Fusibles', 'Cableado', 'Sistema de Carga', 'Otros'],
  Carroceria: ['Parabrisas', 'Vidrios', 'Espejos', 'Puertas', 'Capot', 'Baul', 'Parachoques', 'Guardabarros', 'Pintura', 'Carroceria General', 'Otros'],
  Direccion: ['Caja de Direccion', 'Cremallera', 'Bomba Hidraulica', 'Aceite de Direccion', 'Tirantes', 'Terminales', 'Volante', 'Columna de Direccion', 'Otros'],
  Escape: ['Silenciador', 'Caño de Escape', 'Catalizador', 'Sensor de Oxigeno', 'Colector de Escape', 'Soportes', 'Otros'],
  Refrigeracion: ['Radiador', 'Termostato', 'Bomba de Agua', 'Liquido Refrigerante', 'Mangueras', 'Ventilador', 'Electro Ventilador', 'Sensor de Temperatura', 'Otros'],
  Otros: ['General', 'Revision Completa', 'Service', 'Otros']
};

const Diagnostics: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isCreatePage = useMatch('/diagnostics/new') !== null;
  const [diagnostics, setDiagnostics] = useState<VehicleDiagnosis[]>([]);
  // Interruptor "cortesía sistemática": auto-crear checklist al iniciar cada OT
  const [autoCreate, setAutoCreate] = useState<boolean | null>(null);
  // Vigencia del checklist (días): dentro del plazo, el Taller lo muestra como
  // vigente en vez de pedir uno nuevo (no bloquea rehacerlo)
  const [validityDays, setValidityDays] = useState<string>('');
  // KPIs de conversión del módulo (últimos 90 días)
  const [stats, setStats] = useState<{ total: number; enviados: number; abiertos: number; presupuestados: number; aprobados: number; generado: number } | null>(null);
  // Editor del template del checklist (secciones/puntos, admin). Cada punto
  // puede tener un servicio enlazado: al generar el presupuesto entra con su
  // precio; sin enlace (cubiertas, amortiguadores...) queda libre en $0.
  const [tplOpen, setTplOpen] = useState(false);
  const [tplSections, setTplSections] = useState<Array<{ key: string; label: string; points: Array<{ key: string; label: string; productId?: string; productName?: string }> }>>([]);
  const [tplSaving, setTplSaving] = useState(false);
  const [tplServices, setTplServices] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    if (isCreatePage) return;
    axios.get('/api/diagnostics/stats').then(res => setStats(res.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreatePage]);

  const openTemplateEditor = async () => {
    try {
      const res = await axios.get('/api/diagnostics/checklist-template');
      setTplSections(JSON.parse(JSON.stringify(res.data.sections || [])));
      // Servicios para enlazar (solo servicios, no productos)
      axios.get('/api/products?limit=all')
        .then(r => {
          const arr = r.data.products || r.data || [];
          setTplServices(arr
            .filter((p: any) => p.isService || p.category === 'servicio' || String(p.type || '') === 'servicio')
            .map((p: any) => ({ id: p.id, name: p.name }))
            .sort((a: any, b: any) => a.name.localeCompare(b.name, 'es')));
        })
        .catch(() => setTplServices([]));
      setTplOpen(true);
    } catch {
      toast.error('No se pudo cargar el checklist');
    }
  };

  const saveTemplate = async () => {
    const cleaned = tplSections
      .map(s => ({ ...s, label: s.label.trim(), points: s.points.map(p => ({ ...p, label: p.label.trim() })).filter(p => p.label) }))
      .filter(s => s.label && s.points.length > 0);
    if (!cleaned.length) {
      toast.error('El checklist necesita al menos una sección con un punto');
      return;
    }
    setTplSaving(true);
    try {
      await axios.put('/api/diagnostics/settings/checklist-template', { sections: cleaned });
      toast.success('Checklist actualizado — aplica a los diagnósticos nuevos');
      setTplOpen(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'No se pudo guardar');
    } finally {
      setTplSaving(false);
    }
  };

  useEffect(() => {
    if (!hasPermission('manage_settings')) return;
    axios.get('/api/diagnostics/settings/auto-create')
      .then(res => setAutoCreate(!!res.data.enabled))
      .catch(() => {});
    axios.get('/api/diagnostics/settings/validity-days')
      .then(res => setValidityDays(String(res.data?.days ?? 60)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveValidityDays = async () => {
    const n = parseInt(validityDays, 10);
    if (!Number.isFinite(n) || n < 0) { toast.error('Ingresá una cantidad de días válida'); return; }
    try {
      await axios.put('/api/diagnostics/settings/validity-days', { days: n });
      toast.success(n === 0 ? 'Vigencia desactivada: cada visita pide checklist nuevo' : `Un checklist vale ${n} días`);
    } catch {
      toast.error('No se pudo guardar la vigencia');
    }
  };

  const toggleAutoCreate = async (enabled: boolean) => {
    setAutoCreate(enabled);
    try {
      await axios.put('/api/diagnostics/settings/auto-create', { enabled });
      toast.success(enabled
        ? 'Checklist automático activado: cada OT que se inicia crea su diagnóstico'
        : 'Checklist automático desactivado');
    } catch {
      setAutoCreate(!enabled);
      toast.error('No se pudo guardar la configuración');
    }
  };
  const [clients, setClients] = useState<Client[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDiagnosis, setSelectedDiagnosis] = useState<VehicleDiagnosis | null>(null);
  const [diagnosisItems, setDiagnosisItems] = useState<DiagnosisItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // Sesion de fotos por QR: el celular sube a un token y las fotos caen en la incidencia
  const [qrSession, setQrSession] = useState<{ itemIndex: number; token: string; dataUrl: string; received: number } | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q');
    if (query && query !== searchTerm) {
      setSearchTerm(query);
    }
    const status = params.get('status');
    if (status) {
      setStatusFilter(status);
    }
  }, [location.search, searchTerm]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewDiagnosis, setViewDiagnosis] = useState<VehicleDiagnosis | null>(null);
  const [viewItems, setViewItems] = useState<DiagnosisItem[]>([]);

  const { register, handleSubmit, reset, formState: { errors }, setValue, watch, control } = useForm();
  const selectedClientId = watch('clientId');
  const selectedVehicleId = watch('vehicleId');
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [vehiclePrefillPlate, setVehiclePrefillPlate] = useState('');

  // Cargar diagnosticos
  const fetchDiagnostics = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/diagnostics');
      const diagnosticsList = response.data.diagnostics || [];
      setDiagnostics(diagnosticsList);
    } catch (error) {
      console.error('Error fetching diagnostics:', error);
      toast.error('Error al cargar los diagnosticos');
    } finally {
      setLoading(false);
    }
  };

  // Cargar clientes
  const fetchClients = async () => {
    try {
      const response = await axios.get('/api/clients?limit=10000');
      setClients(response.data.clients.map((c: any) => ({
        id: c.id,
        name: c.name || c.firstName + ' ' + c.lastName || c.companyName,
        email: c.email,
        phone: c.phone,
        vehicles: (c.vehicles || []).map((v: any) => ({
          id: v.id,
          plate: v.licensePlate || v.plate,
          brand: v.brand || v.make,
          model: v.model,
          year: v.year
        }))
      })));
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Error al cargar los clientes');
    }
  };

  const fetchVehiclesByClient = async (clientId: string) => {
    const response = await axios.get(`/api/vehicles?clientId=${clientId}`);
    const list = Array.isArray(response.data) ? response.data : (response.data?.vehicles || []);
    return (list || []).map((v: any) => ({
      id: v.id,
      plate: v.licensePlate || v.plate,
      brand: v.make || v.brand,
      model: v.model,
      year: v.year,
      mileage: v.mileage != null ? Number(v.mileage) : undefined
    })) as Vehicle[];
  };

  useEffect(() => {
    if (!selectedClientId) return;
    const socket = getAppSocket();

    const handleVehiclesUpdate = async (payload: any) => {
      if (!selectedClientId) return;
      if (payload?.clientId && payload.clientId !== selectedClientId) return;
      try {
        const nextVehicles = await fetchVehiclesByClient(selectedClientId);
        setVehicles(nextVehicles);

        const vehicleIds = new Set(nextVehicles.map((v) => v.id));
        const current = selectedVehicleId || '';

        if (current && !vehicleIds.has(current)) {
          setValue('vehicleId', nextVehicles[0]?.id || '');
          return;
        }

        if (!selectedVehicleId || selectedVehicleId === 'none') {
          setValue('vehicleId', nextVehicles[0]?.id || '');
        }
      } catch (error) {
        console.error('Error refreshing vehicles:', error);
      }
    };

    socket.on('vehicles:update', handleVehiclesUpdate);
    return () => {
      socket.off('vehicles:update', handleVehiclesUpdate);
    };
  }, [selectedClientId, selectedVehicleId, setValue]);

  // Actualizar vehiculos cuando se selecciona un cliente (siempre desde API para evitar estado desactualizado)
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!selectedClientId) {
        setVehicles([]);
        if (selectedVehicleId) {
          setValue('vehicleId', '');
        }
        return;
      }

      try {
        const nextVehicles = await fetchVehiclesByClient(selectedClientId);
        if (cancelled) return;

        setVehicles(nextVehicles);

        const vehicleIds = new Set(nextVehicles.map((v) => v.id));
        const current = selectedVehicleId || '';

        if (current && !vehicleIds.has(current)) {
          setValue('vehicleId', nextVehicles[0]?.id || '');
          return;
        }

        if (!selectedVehicleId || selectedVehicleId === 'none') {
          setValue('vehicleId', nextVehicles[0]?.id || '');
        }
      } catch (error) {
        console.error('Error fetching vehicles:', error);
        if (!cancelled) {
          setVehicles([]);
          setValue('vehicleId', '');
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [selectedClientId, selectedVehicleId, setValue]);

  // Precargar el ultimo kilometraje conocido del vehiculo elegido (si el campo esta vacio)
  const mileageValue = watch('mileage');
  const selectedVehicle = vehicles.find((v) => v.id === selectedVehicleId);
  useEffect(() => {
    if (!selectedVehicleId) return;
    const v = vehicles.find((x) => x.id === selectedVehicleId);
    if (v?.mileage && !mileageValue) {
      setValue('mileage', v.mileage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicleId, vehicles]);

  useEffect(() => {
    fetchDiagnostics();
    fetchClients();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Atajo de teclado para nuevo diagnostico
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Solo activar si no estamos escribiendo en un input
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement
      ) {
        return;
      }

      const key = event.key.toUpperCase();
      if (key === 'N') {
        event.preventDefault();
        if (!isCreatePage) {
          clearForm();
          navigate('/diagnostics/new');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isCreatePage, navigate]);

  // Agregar item de diagnostico
  const addDiagnosisItem = () => {
    setDiagnosisItems([
      ...diagnosisItems,
      {
        category: 'Motor',
        subcategory: '',
        description: '',
        severity: 'media',
        recommendation: '',
        images: []
      }
    ]);
  };

  // Remover item de diagnostico
  const removeDiagnosisItem = (index: number) => {
    setDiagnosisItems(diagnosisItems.filter((_, i) => i !== index));
  };

  // Actualizar item de diagnostico
  const updateDiagnosisItem = (index: number, field: keyof DiagnosisItem, value: any) => {
    const newItems = [...diagnosisItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setDiagnosisItems(newItems);
  };

  // Subir imagen para un item
  const handleImageUpload = async (index: number, file: File) => {
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await axios.post('/api/diagnostics/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const imagePath = response.data.image.path;

      // Agregar imagen al array de imagenes del item
      const newItems = [...diagnosisItems];
      const currentImages = newItems[index].images || [];
      newItems[index] = {
        ...newItems[index],
        images: [...currentImages, imagePath]
      };
      setDiagnosisItems(newItems);

      toast.success('Imagen subida correctamente');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Error al subir la imagen');
    }
  };

  // Subir varias imagenes de una (input multiple)
  const handleFilesSelected = async (index: number, files: FileList | null) => {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      await handleImageUpload(index, file);
    }
  };

  // Abrir sesion de fotos por QR para una incidencia
  const openPhotoQR = async (index: number) => {
    try {
      const response = await axios.post('/api/diagnostics/photo-session');
      const { token, url } = response.data;
      const dataUrl = await QRCode.toDataURL(url, { width: 280, margin: 1 });
      setQrSession({ itemIndex: index, token, dataUrl, received: 0 });
    } catch (error) {
      toast.error('No se pudo generar el QR');
    }
  };

  // Mientras la sesion QR este abierta, cada foto subida desde el celular
  // llega por socket y se agrega a la incidencia correspondiente
  useEffect(() => {
    if (!qrSession) return;
    const socket = getAppSocket();
    const handlePhoto = (payload: any) => {
      if (!payload || payload.token !== qrSession.token) return;
      setDiagnosisItems((prev) => {
        const next = [...prev];
        const item = next[qrSession.itemIndex];
        if (!item) return prev;
        next[qrSession.itemIndex] = { ...item, images: [...(item.images || []), payload.path] };
        return next;
      });
      setQrSession((prev) => (prev ? { ...prev, received: prev.received + 1 } : prev));
    };
    socket.on('diagnostics:photo', handlePhoto);
    return () => {
      socket.off('diagnostics:photo', handlePhoto);
    };
  }, [qrSession?.token, qrSession?.itemIndex]);

  // Remover imagen de un item
  const removeImage = (itemIndex: number, imageIndex: number) => {
    const newItems = [...diagnosisItems];
    const currentImages = newItems[itemIndex].images || [];
    newItems[itemIndex] = {
      ...newItems[itemIndex],
      images: currentImages.filter((_, i) => i !== imageIndex)
    };
    setDiagnosisItems(newItems);
  };

  // Crear diagnostico
  const onSubmit = async (data: any) => {
    try {
      const payload = {
        clientId: data.clientId,
        vehicleId: data.vehicleId || null,
        mileage: data.mileage ? parseInt(data.mileage) : null,
        observations: data.observations || null,
        status: 'open'
      };

      // Crear el diagnóstico y pasar directo al checklist con semáforo
      const response = await axios.post('/api/diagnostics', payload);
      const createdId = response.data?.diagnosis?.id;
      toast.success('Diagnóstico creado — completá el checklist');
      clearForm();
      if (createdId) {
        navigate(`/diagnostics/${createdId}/checklist`);
      } else {
        navigate('/diagnostics');
        setTimeout(() => fetchDiagnostics(), 500);
      }
    } catch (error: any) {
      console.error('Error creating diagnosis:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Error al crear el diagnostico';
      toast.error(errorMessage);
    }
  };

  // Editar diagnostico → editor de checklist
  const handleEdit = (diagnosis: VehicleDiagnosis) => {
    navigate(`/diagnostics/${diagnosis.id}/checklist`);
  };

  // Actualizar diagnostico
  const onUpdate = async (data: any) => {
    try {
      if (!selectedDiagnosis) return;

      if (diagnosisItems.length === 0) {
        toast.error('Debe agregar al menos una incidencia');
        return;
      }

      const payload = {
        vehicleId: data.vehicleId === 'none' ? null : data.vehicleId,
        mileage: data.mileage ? parseInt(data.mileage) : null,
        observations: data.observations || null,
        status: data.status,
        urgency: data.urgency
      };

      await axios.put(`/api/diagnostics/${selectedDiagnosis.id}`, payload);

      // Actualizar items (eliminar todos y crear nuevos)
      await axios.delete(`/api/diagnostics/items/${selectedDiagnosis.id}`);
      await axios.post(`/api/diagnostics/${selectedDiagnosis.id}/items`, { items: diagnosisItems });

      toast.success('Diagnostico actualizado correctamente');
      setShowEditModal(false);
      clearForm();
      fetchDiagnostics();
    } catch (error: any) {
      console.error('Error updating diagnosis:', error);
      toast.error(error.response?.data?.error || 'Error al actualizar el diagnostico');
    }
  };

  // Eliminar diagnostico
  const handleDelete = async (id: string) => {
    if (!await confirmDialog('¿Esta seguro de eliminar este diagnostico?')) return;

    try {
      await axios.delete(`/api/diagnostics/${id}`);
      toast.success('Diagnostico eliminado correctamente');
      fetchDiagnostics();
    } catch (error) {
      console.error('Error deleting diagnosis:', error);
      toast.error('Error al eliminar el diagnostico');
    }
  };

  // Cambiar estado
  const handleChangeStatus = async (diagnosis: VehicleDiagnosis, newStatus: VehicleDiagnosis['status']) => {
    try {
      await axios.put(`/api/diagnostics/${diagnosis.id}`, { status: newStatus });
      toast.success('Estado actualizado correctamente');
      fetchDiagnostics();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error al actualizar el estado');
    }
  };

  // Limpiar formulario
  const clearForm = () => {
    reset({ status: 'open', urgency: 'media', vehicleId: '' });
    setDiagnosisItems([]);
    setSelectedDiagnosis(null);
    setVehicles([]);
  };

  useEffect(() => {
    if (isCreatePage) {
      setShowEditModal(false);
      clearForm();
    }
  }, [isCreatePage]);

  const handleVehicleCreated = async (created: any) => {
    const fresh = await fetchVehiclesByClient(selectedClientId);
    setVehicles(fresh);
    if (created?.id) {
      setValue("vehicleId", created.id);
    }
  };

  useEffect(() => {
    if (!selectedClientId && vehicleModalOpen) {
      setVehicleModalOpen(false);
    }
  }, [selectedClientId, vehicleModalOpen]);

  const vehicleCreateDialog = (
    <VehicleCreateDialog
      open={vehicleModalOpen}
      onOpenChange={setVehicleModalOpen}
      clientId={selectedClientId}
      initialPlate={vehiclePrefillPlate}
      onCreated={handleVehicleCreated}
    />
  );

  // Modal QR para fotos desde el celular (se usa en el alta y en el modal de edicion)
  const photoQrDialog = (
    <Dialog open={!!qrSession} onOpenChange={(open) => { if (!open) setQrSession(null); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Fotos desde el celular</DialogTitle>
          <DialogDescription>
            Escanea el QR con la camara del celular, saca las fotos y aparecen solas en la incidencia #{(qrSession?.itemIndex ?? 0) + 1}.
          </DialogDescription>
        </DialogHeader>
        {qrSession && (
          <div className="flex flex-col items-center gap-3 py-2">
            <div className="rounded-xl border bg-white p-3">
              <img src={qrSession.dataUrl} alt="QR para subir fotos" className="h-56 w-56" />
            </div>
            <p className="text-sm text-muted-foreground text-center">
              El link vence en 30 minutos y no requiere iniciar sesion.
            </p>
            <div className={`text-sm font-medium ${qrSession.received > 0 ? 'text-emerald-600' : 'text-muted-foreground'}`}>
              {qrSession.received > 0
                ? `${qrSession.received} foto${qrSession.received > 1 ? 's' : ''} recibida${qrSession.received > 1 ? 's' : ''}`
                : 'Esperando fotos...'}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button type="button" onClick={() => setQrSession(null)}>Listo</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // Ver detalles del diagnostico
  const handleView = async (diagnosis: VehicleDiagnosis) => {
    try {
      const response = await axios.get(`/api/diagnostics/${diagnosis.id}/items`);
      setViewDiagnosis(diagnosis);
      setViewItems(response.data.items || []);
      setShowViewModal(true);
    } catch (error) {
      console.error('Error loading diagnosis details:', error);
      toast.error('Error al cargar los detalles del diagnostico');
    }
  };

  // Ver PDF en nueva pestana
  const handleViewPDF = async (diagnosis: VehicleDiagnosis) => {
    try {
      toast.loading('Generando PDF...');

      // Generar token temporal para el PDF
      const response = await axios.post(`/api/diagnostics/${diagnosis.id}/pdf-token`);
      const { url } = response.data;

      // Construir URL completa del backend
      const backendUrl = axios.defaults.baseURL || window.location.origin;
      const fullUrl = `${backendUrl}${url}`;

      // En el celular openPdf abre la hoja con "Imprimir en el local" y "Descargar"
      // (antes el iPhone abría el PDF afuera del sistema, sin poder imprimir en el local)
      const pdfResponse = await axios.get(fullUrl, { responseType: 'blob' });
      const filename = `Diagnostico-${diagnosis.diagnosisNumber || diagnosis.id}.pdf`;
      const result = openPdf({ blob: pdfResponse.data, filename });

      toast.dismiss();
      if (result === 'downloaded') toast.success('PDF descargado');
    } catch (error) {
      console.error('Error opening PDF:', error);
      toast.dismiss();
      toast.error('Error al abrir el PDF');
    }
  };

  // Enviar por Email
  const handleSendEmail = async (diagnosis: VehicleDiagnosis) => {
    if (!diagnosis.clientEmail) {
      toast.error('El cliente no tiene email registrado');
      return;
    }

    if (!await confirmDialog(`¿Enviar diagnostico por email a ${diagnosis.clientEmail}?`)) {
      return;
    }

    try {
      toast.loading('Enviando email...');
      await axios.post(`/api/diagnostics/${diagnosis.id}/send-email`);
      toast.dismiss();
      toast.success('Diagnostico enviado por email correctamente');
      fetchDiagnostics();
    } catch (error: any) {
      console.error('Error sending email:', error);
      toast.dismiss();
      toast.error(error.response?.data?.error || 'Error al enviar el email');
    }
  };

  // Enviar por WhatsApp
  const handleSendWhatsApp = async (diagnosis: VehicleDiagnosis) => {
    if (!diagnosis.clientPhone) {
      toast.error('El cliente no tiene telefono registrado');
      return;
    }

    if (!await confirmDialog(`¿Enviar diagnostico por WhatsApp a ${diagnosis.clientPhone}?`)) {
      return;
    }

    try {
      toast.loading('Enviando por WhatsApp...');
      await axios.post(`/api/diagnostics/${diagnosis.id}/send-whatsapp`);
      toast.dismiss();
      toast.success('Diagnostico enviado por WhatsApp correctamente');
      fetchDiagnostics();
    } catch (error: any) {
      console.error('Error sending WhatsApp:', error);
      toast.dismiss();
      toast.error(error.response?.data?.error || 'Error al enviar por WhatsApp');
    }
  };

  // Crear presupuesto desde diagnostico
  const handleCreateBudget = async (diagnosis: VehicleDiagnosis) => {
    try {
      // Obtener items del diagnostico: al presupuesto van SOLO los puntos con
      // problema (atención/urgente); los marcados "Bien" no se cotizan
      const response = await axios.get(`/api/diagnostics/${diagnosis.id}/items`);
      const needsWork = (it: any) =>
        ['atencion', 'urgente'].includes(String(it.state || '')) ||
        (!it.state && ['media', 'alta', 'critica'].includes(String(it.severity || '')));
      // Servicio enlazado por punto (configurado en el template): entra con precio
      let linkByKey: Record<string, string> = {};
      try {
        const tpl = await axios.get('/api/diagnostics/checklist-template');
        (tpl.data.sections || []).forEach((s: any) => (s.points || []).forEach((p: any) => {
          if (p.productId) linkByKey[p.key] = p.productId;
        }));
      } catch { /* sin template → todos libres */ }
      const items = (response.data.items || [])
        .filter(needsWork)
        .map((it: any) => ({ ...it, linkedProductId: (it.pointKey && linkByKey[it.pointKey]) || '' }));
      if (items.length === 0) {
        toast.info('El checklist no tiene puntos en atención o urgente: el presupuesto arranca vacío');
      }

      // Preparar datos para el presupuesto
      const budgetData = {
        clientId: diagnosis.clientId,
        vehicleId: diagnosis.vehicleId,
        // Si el diagnóstico nació de una OT abierta, el presupuesto queda
        // enlazado a ese turno (items al turno al aprobar, remito al terminar)
        appointmentId: (diagnosis as any).appointmentId || null,
        relatedDiagnosisId: diagnosis.id,
        diagnosisNumber: diagnosis.diagnosisNumber,
        clientName: diagnosis.clientName,
        vehiclePlate: diagnosis.vehiclePlate,
        vehicleBrand: diagnosis.vehicleBrand,
        vehicleModel: diagnosis.vehicleModel,
        vehicleYear: diagnosis.vehicleYear,
        observations: diagnosis.observations,
        items: items
      };

      // Guardar en sessionStorage para que Budgets lo use
      sessionStorage.setItem('budgetFromDiagnosis', JSON.stringify(budgetData));

      toast.success('Preparando presupuesto...');

      // Navegar a presupuestos
      navigate('/budgets', { state: { fromDiagnosis: true } });
    } catch (error: any) {
      console.error('Error preparing budget:', error);
      toast.error('Error al preparar el presupuesto');
    }
  };

  // Filtrar diagnosticos (chips de estado + busqueda de texto)
  const statusCounts = diagnostics.reduce<Record<string, number>>((acc, d) => {
    acc[d.status] = (acc[d.status] || 0) + 1;
    return acc;
  }, {});
  const STATUS_CHIPS: Array<{ value: string; label: string }> = [
    { value: 'all', label: 'Todos' },
    { value: 'open', label: 'Abiertos' },
    { value: 'delivered', label: 'Entregados' },
    { value: 'budgeted', label: 'Presupuestados' },
    { value: 'approved', label: 'Aprobados' },
    { value: 'closed', label: 'Cerrados' },
    { value: 'not_converted', label: 'No convertidos' }
  ];
  const filteredDiagnostics = diagnostics.filter((diagnosis) => {
    if (statusFilter !== 'all' && diagnosis.status !== statusFilter) {
      return false;
    }
    if (!searchTerm.trim()) {
      return true;
    }

    const search = searchTerm.toLowerCase();
    return (
      diagnosis.diagnosisNumber?.toLowerCase().includes(search) ||
      diagnosis.clientName?.toLowerCase().includes(search) ||
      diagnosis.vehiclePlate?.toLowerCase().includes(search) ||
      diagnosis.vehicleBrand?.toLowerCase().includes(search) ||
      diagnosis.vehicleModel?.toLowerCase().includes(search)
    );
  });

  const totalResults = filteredDiagnostics.length;
  const totalPages = Math.max(1, Math.ceil(totalResults / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDiagnostics = filteredDiagnostics.slice(startIndex, endIndex);
  const startResult = totalResults === 0 ? 0 : startIndex + 1;
  const endResult = Math.min(endIndex, totalResults);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Funciones de navegacion de paginacion
  const goToFirstPage = () => setCurrentPage(1);
  const goToPreviousPage = () => setCurrentPage((prev) => Math.max(1, prev - 1));
  const goToNextPage = () => setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  const goToLastPage = () => setCurrentPage(totalPages);

  // Obtener color de badge de estado
  const getStatusBadge = (status: string) => {
    const map = {
      draft: {
        label: 'Borrador',
        className: 'bg-muted text-muted-foreground border-transparent'
      },
      open: {
        label: 'Abierto',
        className: 'bg-slate-100 text-slate-700 border-slate-200'
      },
      delivered: {
        label: 'Entregado',
        className: 'bg-blue-100 text-blue-700 border-blue-200'
      },
      budgeted: {
        label: 'Presupuestado',
        className: 'bg-indigo-100 text-indigo-700 border-indigo-200'
      },
      approved: {
        label: 'Aprobado',
        className: 'bg-emerald-100 text-emerald-700 border-emerald-200'
      },
      closed: {
        label: 'Cerrado',
        className: 'bg-zinc-200 text-zinc-700 border-zinc-300'
      },
      not_converted: {
        label: 'No convertido',
        className: 'bg-rose-100 text-rose-700 border-rose-200'
      },
      completed: {
        label: 'Aprobado',
        className: 'bg-emerald-100 text-emerald-700 border-emerald-200'
      }
    } as const;

    const config = map[status as keyof typeof map] || map.open;
    return (
      <Badge variant="outline" className={`text-xs font-medium ${config.className}`}>
        {config.label}
      </Badge>
    );
  };

  const getUrgencyBadge = (urgency?: string) => {
    const map = {
      baja: { label: 'Baja', className: 'bg-green-100 text-green-700 border-green-200' },
      media: { label: 'Media', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
      alta: { label: 'Alta', className: 'bg-orange-100 text-orange-700 border-orange-200' },
      critica: { label: 'Critica', className: 'bg-red-100 text-red-700 border-red-200' }
    } as const;
    const config = map[urgency as keyof typeof map] || map.media;
    return (
      <Badge variant="outline" className={`text-xs font-medium ${config.className}`}>
        {config.label}
      </Badge>
    );
  };

  const getStatusDaysLabel = (diagnosis: VehicleDiagnosis) => {
    const base = diagnosis.statusChangedAt || diagnosis.createdAt || diagnosis.updatedAt;
    if (!base) return '-';
    const start = new Date(base).getTime();
    if (Number.isNaN(start)) return '-';
    const days = Math.max(0, Math.floor((Date.now() - start) / (1000 * 60 * 60 * 24)));
    if (days === 0) return 'Hoy';
    if (days === 1) return '1 dia';
    return `${days} dias`;
  };

  // Obtener color de severidad
  const getSeverityColor = (severity: string) => {
    const colors = {
      baja: 'text-green-600 bg-green-100',
      media: 'text-yellow-600 bg-yellow-100',
      alta: 'text-orange-600 bg-orange-100',
      critica: 'text-red-600 bg-red-100'
    };
    return colors[severity as keyof typeof colors] || 'text-gray-600 bg-gray-100';
  };

  const getClientDisplayName = (client: Client) => client.name || 'Sin nombre';

  const toClientComboboxItem = (client: Client) => ({
    id: client.id,
    firstName: getClientDisplayName(client),
    lastName: '',
    companyName: undefined,
    type: 'individual' as const,
    email: client.email,
    phone: client.phone
  });

  const openCreateClientPage = (query: string) => {
    const returnTo = `${location.pathname}${location.search || ''}`;
    const params = new URLSearchParams();
    params.set('returnTo', returnTo);
    if (query.trim()) params.set('q', query.trim());
    navigate(`/clients/new?${params.toString()}`);
  };

  const openCreateVehicleDialog = (query: string) => {
    if (!selectedClientId) {
      toast.error('Seleccione un cliente primero');
      return;
    }
    setVehiclePrefillPlate(query || '');
    setVehicleModalOpen(true);
  };

  if (isCreatePage) {
    return (
      <div className="space-y-6 p-4 sm:p-6 text-foreground">
        <div className="mx-auto w-full max-w-5xl space-y-6">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Nuevo diagnostico</h1>
              <p className="text-sm text-muted-foreground">
                Completa los datos y agrega incidencias para generar el diagnostico.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => {
                clearForm();
                navigate('/diagnostics');
              }}
            >
              Volver
            </Button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="clientId">
                  Cliente <span className="text-destructive">*</span>
                </Label>
                <Controller
                  control={control}
                  name="clientId"
                  rules={{ required: 'Cliente es requerido' }}
                  render={({ field }) => (
                    <ClientCombobox
                      clients={clients.map(toClientComboboxItem)}
                      selectedClient={clients.map(toClientComboboxItem).find((client) => client.id === field.value) || null}
                      onSelect={(client) => {
                        field.onChange(client?.id || '');
                        setValue('vehicleId', '');
                      }}
                      onCreateClient={openCreateClientPage}
                      placeholder="Buscar cliente..."
                      getClientName={(client) =>
                        client.companyName || `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'Sin nombre'
                      }
                    />
                  )}
                />
                {errors.clientId && (
                  <p className="text-sm text-destructive">{errors.clientId.message?.toString()}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="vehicleId">
                  Vehículo <span className="text-destructive">*</span>
                </Label>
                <Controller
                  control={control}
                  name="vehicleId"
                  rules={{
                    required: 'Vehículo es requerido',
                    validate: (value) => (value && value !== 'none') || 'Vehículo es requerido'
                  }}
                  render={({ field }) => (
                    <VehicleCombobox
                      vehicles={vehicles}
                      selectedVehicle={vehicles.find((vehicle) => vehicle.id === field.value) || null}
                      onSelect={(vehicle) => field.onChange(vehicle?.id || '')}
                      onCreateVehicle={openCreateVehicleDialog}
                      placeholder={selectedClientId ? 'Buscar vehículo...' : 'Seleccione un cliente primero'}
                      disabled={!selectedClientId}
                    />
                  )}
                />
                {errors.vehicleId && (
                  <p className="text-sm text-destructive">{errors.vehicleId.message?.toString()}</p>
                )}
              </div>
            </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="mileage">Kilometraje</Label>
                    <Input
                      id="mileage"
                      type="number"
                      {...register('mileage')}
                      placeholder="Ej: 50000"
                    />
                    {selectedVehicle?.mileage ? (
                      Number(mileageValue) > 0 && Number(mileageValue) < selectedVehicle.mileage ? (
                        <p className="text-xs text-amber-600">
                          Ojo: es menor al último registrado ({selectedVehicle.mileage.toLocaleString()} km).
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          Último registrado: {selectedVehicle.mileage.toLocaleString()} km
                        </p>
                      )
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="urgency">Urgencia</Label>
                    <Controller
                      control={control}
                      name="urgency"
                      defaultValue="media"
                      render={({ field }) => (
                        <Select value={field.value || 'media'} onValueChange={field.onChange}>
                          <SelectTrigger id="urgency">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {SEVERITIES.map((severity) => (
                              <SelectItem key={severity.value} value={severity.value}>
                                {severity.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="observations">Observaciones</Label>
                  <Textarea
                    id="observations"
                    {...register('observations')}
                    rows={3}
                    placeholder="Observaciones generales (opcional)..."
                  />
                </div>

                <div className="space-y-3">
                  <div>
                    <Label className="text-base font-medium">Checklist de inspección</Label>
                    <p className="text-sm text-muted-foreground">
                      Al crear el diagnóstico se abre el checklist con semáforo (Bien / Atención / Urgente) para tildar punto por punto, con fotos.
                    </p>
                  </div>

                  {diagnosisItems.length === 0 ? (
                    <></>
                  ) : (
                    <div className="space-y-4">
                      {diagnosisItems.map((item, index) => (
                        <div key={index} className="rounded-xl border p-4 space-y-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 flex-1">
                              <div className="space-y-2">
                                <Label className="text-sm font-medium">Categoria</Label>
                                <Select
                                  value={item.category}
                                  onValueChange={(value) => updateDiagnosisItem(index, 'category', value)}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Categoria" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {CATEGORIES.map((category) => (
                                      <SelectItem key={category} value={category}>
                                        {category}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>

                              <div className="space-y-2">
                                <Label className="text-sm font-medium">Subcategoria</Label>
                                <Select
                                  value={item.subcategory || ''}
                                  onValueChange={(value) => updateDiagnosisItem(index, 'subcategory', value)}
                                  disabled={!item.category}
                                >
                                  <SelectTrigger className={!item.category ? 'opacity-50 cursor-not-allowed' : ''}>
                                    <SelectValue placeholder={item.category ? 'Subcategoria' : 'Seleccione categoria'} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {(SUBCATEGORIES[item.category] || []).map((sub) => (
                                      <SelectItem key={sub} value={sub}>
                                        {sub}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>

                              <div className="space-y-2">
                                <Label className="text-sm font-medium">Severidad</Label>
                                <Select
                                  value={item.severity}
                                  onValueChange={(value) => updateDiagnosisItem(index, 'severity', value)}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Severidad" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {SEVERITIES.map((severity) => (
                                      <SelectItem key={severity.value} value={severity.value}>
                                        {severity.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            <Tip label="Eliminar incidencia">
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="size-8"
                                onClick={() => removeDiagnosisItem(index)}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                            </Tip>
                          </div>

                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                              <Label className="text-sm font-medium">Descripcion</Label>
                              <Textarea
                                value={item.description}
                                onChange={(e) => updateDiagnosisItem(index, 'description', e.target.value)}
                                rows={2}
                                placeholder="Describa el problema encontrado..."
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm font-medium">Recomendacion</Label>
                              <Textarea
                                value={item.recommendation || ''}
                                onChange={(e) => updateDiagnosisItem(index, 'recommendation', e.target.value)}
                                rows={2}
                                placeholder="Recomendacion de reparacion o mantenimiento..."
                              />
                            </div>
                          </div>

                          <div className="space-y-3">
                            <Label className="text-sm font-medium">Imagenes (opcional)</Label>
                            <div className="flex flex-wrap items-center gap-2">
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                id={`image-upload-create-${index}`}
                                onChange={(e) => {
                                  handleFilesSelected(index, e.target.files);
                                  e.target.value = '';
                                }}
                                className="hidden"
                              />
                              <label
                                htmlFor={`image-upload-create-${index}`}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-muted cursor-pointer transition-colors text-muted-foreground hover:text-foreground hover:border-foreground/20 hover:bg-muted/50"
                              >
                                <PhotoIcon className="h-5 w-5" />
                                <span className="text-sm">Agregar imagen</span>
                              </label>
                              <button
                                type="button"
                                onClick={() => openPhotoQR(index)}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-muted cursor-pointer transition-colors text-muted-foreground hover:text-foreground hover:border-foreground/20 hover:bg-muted/50"
                              >
                                <QrCodeIcon className="h-5 w-5" />
                                <span className="text-sm">Desde el celular</span>
                              </button>
                            </div>

                            {item.images && item.images.length > 0 && (
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {item.images.map((imagePath, imgIndex) => (
                                  <div key={imgIndex} className="relative rounded-lg overflow-hidden border border-border">
                                    <img src={imagePath} alt={`Imagen ${imgIndex + 1}`} className="w-full h-32 object-cover" />
                                    <Tip label="Eliminar imagen">
                                      <button
                                        type="button"
                                        onClick={() => removeImage(index, imgIndex)}
                                        className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                                      >
                                        <XMarkIcon className="h-4 w-4" />
                                      </button>
                                    </Tip>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      clearForm();
                      navigate('/diagnostics');
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit">Crear y abrir checklist</Button>
                </div>
              </form>
          {photoQrDialog}
          {vehicleCreateDialog}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-8 p-4 sm:p-6 text-foreground">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-8">
      <section className="space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Diagnósticos de vehículos</h1>
            {autoCreate !== null && (
              <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1.5">
                <div className="flex items-center gap-2">
                  <Switch checked={autoCreate} onCheckedChange={toggleAutoCreate} />
                  <span className="text-sm text-muted-foreground">
                    Crear checklist automático al iniciar cada OT
                  </span>
                </div>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={openTemplateEditor}>
                  <PencilIcon className="mr-1 h-3.5 w-3.5" /> Editar puntos del checklist
                </Button>
                {/* Vigencia: dentro del plazo el Taller marca el checklist anterior como vigente */}
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  Un checklist vale
                  <input
                    type="number"
                    min={0}
                    max={3650}
                    value={validityDays}
                    onChange={(e) => setValidityDays(e.target.value)}
                    onBlur={saveValidityDays}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); } }}
                    className="h-7 w-16 rounded-md border border-input bg-background px-2 text-center text-sm text-foreground"
                  />
                  días (0 = siempre pedir uno nuevo)
                </label>
              </div>
            )}
          </div>
          <Button
            onClick={() => {
              clearForm();
              navigate('/diagnostics/new');
            }}
            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-bold h-8 rounded-lg gap-1.5 px-3 bg-black dark:bg-white text-white dark:text-black shadow-xs hover:bg-black/90 dark:hover:bg-white/90"
            size="sm"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Nuevo diagnostico
            <kbd className="ml-2 inline-flex items-center justify-center px-1 text-xs font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border leading-none">
              N
            </kbd>
          </Button>
        </div>
        {stats && stats.total > 0 && (
          <div className="grid grid-cols-3 gap-2 md:grid-cols-6">
            {[
              { label: 'Hechos', value: stats.total },
              { label: 'Enviados', value: stats.enviados },
              { label: 'Abiertos', value: stats.abiertos },
              { label: 'Presupuestados', value: stats.presupuestados },
              { label: 'Aprobados', value: stats.aprobados },
              // wide: el monto largo ocupa la fila entera en el telefono para no desbordar
              { label: 'Generado', value: `$${Number(stats.generado).toLocaleString('es-AR')}`, wide: true }
            ].map(kpi => (
              <div key={kpi.label} className={`min-w-0 rounded-xl border bg-card px-3 py-2 ${(kpi as any).wide ? 'col-span-3 md:col-span-1' : ''}`}>
                <p className="truncate text-lg font-bold leading-tight">{kpi.value}</p>
                <p className="truncate text-xs text-muted-foreground">{kpi.label} · 90d</p>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative w-full sm:max-w-md">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por numero, cliente o vehiculo..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="pl-9"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {STATUS_CHIPS.map((chip) => {
            const count = chip.value === 'all' ? diagnostics.length : (statusCounts[chip.value] || 0);
            if (chip.value !== 'all' && count === 0) return null;
            const active = statusFilter === chip.value;
            return (
              <button
                key={chip.value}
                type="button"
                onClick={() => setStatusFilter(chip.value)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  active
                    ? 'bg-foreground text-background border-foreground'
                    : 'bg-background text-muted-foreground border-border hover:text-foreground hover:border-foreground/30'
                }`}
              >
                {chip.label}
                <span className={active ? 'opacity-80' : 'text-muted-foreground/70'}>{count}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Editor del template del checklist (admin) */}
      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Puntos del checklist</DialogTitle>
            <DialogDescription>
              Editá secciones y puntos. Los cambios aplican a los checklists nuevos; los ya cargados no se tocan.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] space-y-3 overflow-y-auto pr-1">
            {tplSections.map((sec, si) => (
              <div key={sec.key} className="space-y-1.5 rounded-lg border p-2">
                <div className="flex items-center gap-2">
                  <Input
                    value={sec.label}
                    onChange={e => setTplSections(prev => prev.map((s, i) => (i === si ? { ...s, label: e.target.value } : s)))}
                    className="h-8 flex-1 font-semibold"
                    placeholder="Nombre de la sección"
                  />
                  <Tip label="Quitar sección">
                    <button
                      type="button"
                      className="flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground hover:text-red-500"
                      onClick={() => setTplSections(prev => prev.filter((_, i) => i !== si))}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </Tip>
                </div>
                {sec.points.map((p, pi) => (
                  <div key={p.key} className="ml-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <Input
                        value={p.label}
                        onChange={e => setTplSections(prev => prev.map((s, i) =>
                          i === si ? { ...s, points: s.points.map((x, j) => (j === pi ? { ...x, label: e.target.value } : x)) } : s
                        ))}
                        className="h-8 flex-1 text-sm"
                        placeholder="Punto a revisar"
                      />
                      <Tip label="Quitar punto">
                        <button
                          type="button"
                          className="flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground hover:text-red-500"
                          onClick={() => setTplSections(prev => prev.map((s, i) =>
                            i === si ? { ...s, points: s.points.filter((_, j) => j !== pi) } : s
                          ))}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </Tip>
                    </div>
                    {/* Servicio enlazado (opcional): entra al presupuesto con su precio */}
                    <Select
                      value={p.productId || 'none'}
                      onValueChange={v => setTplSections(prev => prev.map((s, i) =>
                        i === si ? {
                          ...s,
                          points: s.points.map((x, j) => (j === pi
                            ? { ...x, productId: v === 'none' ? undefined : v, productName: v === 'none' ? undefined : tplServices.find(sv => sv.id === v)?.name }
                            : x))
                        } : s
                      ))}
                    >
                      <SelectTrigger className={`h-7 w-full text-xs ${p.productId ? '' : 'text-muted-foreground'}`}>
                        <SelectValue placeholder="Sin servicio enlazado (se cotiza a mano)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin servicio enlazado (se cotiza a mano)</SelectItem>
                        {tplServices.map(sv => (
                          <SelectItem key={sv.id} value={sv.id}>{sv.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
                <Button
                  type="button" variant="ghost" size="sm" className="ml-3 h-7 text-xs"
                  onClick={() => setTplSections(prev => prev.map((s, i) =>
                    i === si ? { ...s, points: [...s.points, { key: `p_${Date.now()}_${s.points.length}`, label: '' }] } : s
                  ))}
                >
                  <PlusIcon className="mr-1 h-3.5 w-3.5" /> Agregar punto
                </Button>
              </div>
            ))}
            <Button
              type="button" variant="outline" size="sm" className="w-full"
              onClick={() => setTplSections(prev => [...prev, { key: `s_${Date.now()}`, label: '', points: [{ key: `p_${Date.now()}_0`, label: '' }] }])}
            >
              <PlusIcon className="mr-1 h-4 w-4" /> Agregar sección
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTplOpen(false)}>Cancelar</Button>
            <Button onClick={saveTemplate} disabled={tplSaving}>{tplSaving ? 'Guardando...' : 'Guardar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lista de diagnosticos */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
            </div>
          ) : totalResults === 0 ? (
            <div className="text-center py-12">
              <ClipboardDocumentCheckIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {searchTerm
                  ? 'No se encontraron diagnosticos para la busqueda ingresada.'
                  : 'No se registraron diagnosticos todavia.'}
              </p>
            </div>
          ) : (
            <>
              {/* Vista de tabla para pantallas medianas y grandes */}
              <div className="hidden md:block relative w-full">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-muted/50 border-b">
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Codigo</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Fecha</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Cliente</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Vehículo</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left cq-hide-mid">Km</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Urgencia</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left cq-hide-mid">Días</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedDiagnostics.map((diagnosis) => (
                      <TableRow key={diagnosis.id} className="border-b hover:bg-muted/50 cursor-pointer">
                        <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                          <div className="font-medium font-mono">{diagnosis.diagnosisNumber || 'S/N'}</div>
                        </TableCell>
                        <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                          <div className="text-muted-foreground">
                            {new Date(diagnosis.createdAt).toLocaleDateString('es-AR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit'
                            })}
                          </div>
                        </TableCell>
                        <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                          <div className="font-medium truncate max-w-[170px] cq-clamp-narrow">{diagnosis.clientName || 'Sin cliente'}</div>
                        </TableCell>
                        <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                          {diagnosis.vehiclePlate ? (
                            <>
                              <div className="font-medium">{diagnosis.vehiclePlate}</div>
                              <div className="text-xs text-muted-foreground truncate max-w-[170px] cq-clamp-narrow">
                                {diagnosis.vehicleBrand} {diagnosis.vehicleModel}
                                {diagnosis.vehicleYear ? ` (${diagnosis.vehicleYear})` : ''}
                              </div>
                            </>
                          ) : (
                            <div className="text-muted-foreground">No especificado</div>
                          )}
                        </TableCell>
                        <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left cq-hide-mid">
                          <div>{diagnosis.mileage ? `${diagnosis.mileage.toLocaleString()} km` : '-'}</div>
                        </TableCell>
                        <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                          {getStatusBadge(diagnosis.status)}
                        </TableCell>
                        <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                          {getUrgencyBadge(diagnosis.urgency)}
                        </TableCell>
                        <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left cq-hide-mid">
                          <div className="text-sm text-muted-foreground">{getStatusDaysLabel(diagnosis)}</div>
                        </TableCell>
                        <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end">
                            <ButtonGroup>
                              <Tip label="Ver detalles">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleView(diagnosis);
                                  }}
                                  className="size-8"
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                              <Tip label="Editar">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(diagnosis);
                                  }}
                                  className="size-8"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                              <Tip label="Ver PDF">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewPDF(diagnosis);
                                  }}
                                  className="size-8"
                                >
                                  <DocumentTextIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                              {diagnosis.clientPhone && (
                                <Tip label={diagnosis.whatsappSent ? `WhatsApp enviado (${diagnosis.whatsappSent})` : 'Enviar por WhatsApp'}>
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSendWhatsApp(diagnosis);
                                    }}
                                    className={`size-8 ${diagnosis.whatsappSent ? 'border-emerald-200 text-emerald-600 bg-emerald-50 hover:bg-emerald-100' : ''}`}
                                  >
                                    <WhatsAppIcon className="h-4 w-4" />
                                  </Button>
                                </Tip>
                              )}
                              {diagnosis.clientEmail && (
                                <Tip label={diagnosis.emailSent ? `Email enviado (${diagnosis.emailSent})` : 'Enviar por email'}>
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSendEmail(diagnosis);
                                    }}
                                    className={`size-8 ${diagnosis.emailSent ? 'border-emerald-200 text-emerald-600 bg-emerald-50 hover:bg-emerald-100' : ''}`}
                                  >
                                    <EnvelopeIcon className="h-4 w-4" />
                                  </Button>
                                </Tip>
                              )}
                              <Tip label={diagnosis.relatedBudgetId ? 'Ver presupuesto' : 'Crear presupuesto'}>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (diagnosis.relatedBudgetId) {
                                      navigate('/budgets');
                                    } else {
                                      handleCreateBudget(diagnosis);
                                    }
                                  }}
                                  className={`size-8 ${diagnosis.relatedBudgetId
                                    ? 'border-emerald-200 text-emerald-600 bg-emerald-50 hover:bg-emerald-100'
                                    : ''}`}
                                >
                                  <CurrencyDollarIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                              <Tip label="Eliminar">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(diagnosis.id);
                                  }}
                                  className="size-8"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                            </ButtonGroup>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Vista de tarjetas para pantallas pequenas */}
              <div className="md:hidden space-y-3">
                {paginatedDiagnostics.map((diagnosis) => (
                  <Card key={diagnosis.id} className="shadow-sm hover:shadow-md transition-shadow duration-200">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-base">#{diagnosis.diagnosisNumber || 'S/N'}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          {getStatusBadge(diagnosis.status)}
                          {getUrgencyBadge(diagnosis.urgency)}
                          {diagnosis.relatedBudgetId && (
                            <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-100 text-xs">
                              Presupuesto
                            </Badge>
                          )}
                          {(diagnosis as any).relatedRemitoNumber && (
                            <Tip label="Trabajo hecho directo (sin presupuesto). La cruz desenlaza el remito.">
                              <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border-emerald-100 text-xs inline-flex items-center gap-1">
                                {(diagnosis as any).relatedRemitoNumber}
                                <button
                                  type="button"
                                  className="hover:text-red-600"
                                  onClick={async (ev) => {
                                    ev.stopPropagation();
                                    if (!await confirmDialog(`¿Desenlazar el remito ${(diagnosis as any).relatedRemitoNumber} de este diagnóstico? Deja de contar en las métricas de conversión directa.`)) return;
                                    try {
                                      await axios.patch(`/api/diagnostics/${diagnosis.id}/link-remito`, { remitoId: null });
                                      toast.success('Remito desenlazado');
                                      fetchDiagnostics();
                                    } catch {
                                      toast.error('No se pudo desenlazar');
                                    }
                                  }}
                                >
                                  <XMarkIcon className="h-3 w-3" />
                                </button>
                              </Badge>
                            </Tip>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {getStatusDaysLabel(diagnosis)} en estado
                        </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <ButtonGroup>
                            <Tip label="Ver detalles">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => handleView(diagnosis)}
                                className="size-8"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </Button>
                            </Tip>
                            <Tip label="Editar">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => handleEdit(diagnosis)}
                                className="size-8"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </Button>
                            </Tip>
                            <Tip label="Ver PDF">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => handleViewPDF(diagnosis)}
                                className="size-8"
                              >
                                <DocumentTextIcon className="h-4 w-4" />
                              </Button>
                            </Tip>
                            {diagnosis.clientPhone && (
                              <Tip label={diagnosis.whatsappSent ? `WhatsApp enviado (${diagnosis.whatsappSent})` : 'Enviar por WhatsApp'}>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => handleSendWhatsApp(diagnosis)}
                                  className={`size-8 ${diagnosis.whatsappSent ? 'border-emerald-200 text-emerald-600 bg-emerald-50 hover:bg-emerald-100' : ''}`}
                                >
                                  <WhatsAppIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                            )}
                            {diagnosis.clientEmail && (
                              <Tip label={diagnosis.emailSent ? `Email enviado (${diagnosis.emailSent})` : 'Enviar por email'}>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={() => handleSendEmail(diagnosis)}
                                  className={`size-8 ${diagnosis.emailSent ? 'border-emerald-200 text-emerald-600 bg-emerald-50 hover:bg-emerald-100' : ''}`}
                                >
                                  <EnvelopeIcon className="h-4 w-4" />
                                </Button>
                              </Tip>
                            )}
                            <Tip label={diagnosis.relatedBudgetId ? 'Ver presupuesto' : 'Crear presupuesto'}>
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => {
                                  if (diagnosis.relatedBudgetId) {
                                    navigate('/budgets');
                                  } else {
                                    handleCreateBudget(diagnosis);
                                  }
                                }}
                                className={`size-8 ${diagnosis.relatedBudgetId
                                  ? 'border-emerald-200 text-emerald-600 bg-emerald-50 hover:bg-emerald-100'
                                  : ''}`}
                              >
                                <CurrencyDollarIcon className="h-4 w-4" />
                              </Button>
                            </Tip>
                            <Tip label="Eliminar">
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => handleDelete(diagnosis.id)}
                                className="size-8"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                            </Tip>
                          </ButtonGroup>
                        </div>
                      </div>
                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Cliente:</span>
                          <span className="font-medium truncate max-w-32">{diagnosis.clientName || 'Sin cliente'}</span>
                        </div>
                        {diagnosis.vehiclePlate && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Vehículo:</span>
                            <span className="truncate max-w-32">{diagnosis.vehiclePlate}</span>
                          </div>
                        )}
                        {diagnosis.mileage && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Kilometraje:</span>
                            <span>{diagnosis.mileage.toLocaleString()} km</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Fecha:</span>
                          <span>
                            {new Date(diagnosis.createdAt).toLocaleDateString('es-AR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Paginacion */}
      {!loading && totalResults > 0 && (
        <div className="flex items-center justify-between px-2">
          <div className="hidden flex-1 text-muted-foreground text-sm md:block">
            Mostrando {startResult}-{endResult} de {totalResults} resultados
          </div>
          <div className="flex items-center space-x-6 lg:space-x-8">
            <div className="flex w-[100px] items-center justify-center font-medium text-sm">
              Pagina {currentPage} de {totalPages}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="icon"
              onClick={goToFirstPage}
              disabled={currentPage === 1}
              className="shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 hidden h-8 w-8 lg:flex"
            >
              <span className="sr-only">Ir a la primera pagina</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <path d="m11 17-5-5 5-5"></path>
                <path d="m18 17-5-5 5-5"></path>
              </svg>
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 h-8 w-8"
            >
              <span className="sr-only">Ir a la pagina anterior</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <path d="m15 18-6-6 6-6"></path>
              </svg>
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 h-8 w-8"
            >
              <span className="sr-only">Ir a la pagina siguiente</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <path d="m9 18 6-6-6-6"></path>
              </svg>
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToLastPage}
              disabled={currentPage === totalPages}
              className="shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 hidden h-8 w-8 lg:flex"
            >
              <span className="sr-only">Ir a la ultima pagina</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <path d="m13 17 5-5-5-5"></path>
                <path d="m6 17 5-5-5-5"></path>
              </svg>
            </Button>
          </div>
        </div>
      )}

      {/* Modal Editar */}
      <Dialog open={showEditModal} onOpenChange={(open) => {
        if (!open) {
          setShowEditModal(false);
          clearForm();
        }
      }}>
        <DialogContent className="max-w-5xl rounded-3xl border-0 shadow-2xl p-0 overflow-hidden max-h-[90vh]">
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">
                Editar Diagnostico
              </DialogTitle>
              <DialogDescription>
                Modifique los datos del diagnostico
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="px-6 pb-6 overflow-y-auto max-h-[calc(90vh-140px)] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
            <form id="diagnosis-form" onSubmit={handleSubmit(onUpdate)} className="space-y-6">
              {/* Cliente y Vehículo */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="clientId" className="font-medium text-sm">
                    Cliente <span className="text-red-500">*</span>
                  </Label>
                  <Controller
                    control={control}
                    name="clientId"
                    rules={{ required: 'Cliente es requerido' }}
                    render={({ field }) => (
                      <Select
                        value={field.value || ''}
                        onValueChange={field.onChange}
                        disabled={showEditModal}
                      >
                        <SelectTrigger id="clientId" className={showEditModal ? 'opacity-50 cursor-not-allowed' : ''}>
                          <SelectValue placeholder="Seleccione un cliente" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.clientId && (
                    <p className="text-sm text-destructive mt-1">{errors.clientId.message?.toString()}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vehicleId" className="font-medium text-sm">Vehículo</Label>
                  <Controller
                    control={control}
                    name="vehicleId"
                    render={({ field }) => (
                      (() => {
                        const vehicleIds = new Set((vehicles || []).map((v) => v.id));
                        const currentValue = field.value || 'none';
                        const isMissing = currentValue && currentValue !== 'none' && !vehicleIds.has(currentValue);
                        const showNoneOption = currentValue === 'none' || (vehicles || []).length === 0;

                        return (
                          <Select
                            value={currentValue}
                            onValueChange={(value) => {
                              if (value === '__create__') {
                                setVehicleModalOpen(true);
                                return;
                              }
                              field.onChange(value);
                            }}
                          >
                            <SelectTrigger id="vehicleId">
                              <SelectValue placeholder="Seleccione un vehiculo" />
                            </SelectTrigger>
                            <SelectContent>
                              {isMissing ? (
                                <SelectItem value={currentValue} disabled>
                                  Vehículo no encontrado
                                </SelectItem>
                              ) : null}
                              {showNoneOption ? <SelectItem value="none">Sin vehículo especifico</SelectItem> : null}
                              <SelectItem value="__create__">Crear vehiculo...</SelectItem>
                              {vehicles.map((vehicle) => (
                                <SelectItem key={vehicle.id} value={vehicle.id}>
                                  {vehicle.plate} - {vehicle.brand} {vehicle.model} ({vehicle.year})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        );
                      })()
                    )}
                  />
                </div>
              </div>

              {/* Kilometraje, Estado y Urgencia */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="mileage" className="font-medium text-sm">Kilometraje</Label>
                  <Input
                    id="mileage"
                    type="number"
                    {...register('mileage')}
                    placeholder="Ej: 50000"
                    className="px-4 py-3 rounded-xl border border-input bg-background"
                  />
                  {Number((selectedDiagnosis as any)?.vehicleMileage) > 0 && Number(mileageValue) > 0 && Number(mileageValue) < Number((selectedDiagnosis as any).vehicleMileage) && (
                    <p className="text-xs text-amber-600">
                      Ojo: es menor al último registrado en la ficha ({Number((selectedDiagnosis as any).vehicleMileage).toLocaleString('es-AR')} km).
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status" className="font-medium text-sm">Estado</Label>
                  <Controller
                    control={control}
                    name="status"
                    render={({ field }) => (
                      <Select
                        value={field.value || 'open'}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger id="status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Abierto</SelectItem>
                          <SelectItem value="delivered">Entregado</SelectItem>
                          <SelectItem value="budgeted">Presupuestado</SelectItem>
                          <SelectItem value="approved">Aprobado</SelectItem>
                          <SelectItem value="closed">Cerrado</SelectItem>
                          <SelectItem value="not_converted">No convertido</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="urgency" className="font-medium text-sm">Urgencia</Label>
                  <Controller
                    control={control}
                    name="urgency"
                    render={({ field }) => (
                      <Select
                        value={field.value || 'media'}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger id="urgency">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="baja">Baja</SelectItem>
                          <SelectItem value="media">Media</SelectItem>
                          <SelectItem value="alta">Alta</SelectItem>
                          <SelectItem value="critica">Critica</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              </div>

              {/* Observaciones */}
              <div className="space-y-2">
                <Label htmlFor="observations" className="font-medium text-sm">Observaciones Generales</Label>
                <Textarea
                  id="observations"
                  {...register('observations')}
                  rows={3}
                  placeholder="Observaciones generales del diagnostico..."
                  className="px-4 py-3 rounded-xl border border-input bg-background"
                />
              </div>

              {/* Items de Diagnostico */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-semibold">Incidencias Detectadas</h4>
                  <Button
                    type="button"
                    onClick={addDiagnosisItem}
                    size="sm"
                    variant="outline"
                    className="h-8"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Agregar Incidencia
                  </Button>
                </div>

                {diagnosisItems.length === 0 ? (
                  <div className="p-4 text-center rounded-lg border-2 border-dashed border-muted text-muted-foreground">
                    No hay incidencias agregadas. Haga clic en "Agregar Incidencia" para comenzar.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {diagnosisItems.map((item, index) => (
                      <div
                        key={index}
                        className="p-4 rounded-lg border bg-card text-foreground"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <h5 className="font-medium text-foreground">Incidencia #{index + 1}</h5>
                          <Button
                            type="button"
                            onClick={() => removeDiagnosisItem(index)}
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="font-medium text-sm">Categoria</Label>
                            <Select
                              value={item.category}
                              onValueChange={(newCategory) => {
                                setDiagnosisItems(prevItems => {
                                  const newItems = [...prevItems];
                                  newItems[index] = {
                                    ...newItems[index],
                                    category: newCategory,
                                    subcategory: ''
                                  };
                                  return newItems;
                                });
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {CATEGORIES.map((cat) => (
                                  <SelectItem key={cat} value={cat}>
                                    {cat}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            <Label className="font-medium text-sm">Subcategoria/Componente</Label>
                            <Select
                              value={item.subcategory || ''}
                              onValueChange={(value) => updateDiagnosisItem(index, 'subcategory', value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Seleccione un componente" />
                              </SelectTrigger>
                              <SelectContent>
                                {SUBCATEGORIES[item.category]?.map((subcat) => (
                                  <SelectItem key={subcat} value={subcat}>
                                    {subcat}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="mt-4 space-y-2">
                          <Label className="font-medium text-sm">Severidad</Label>
                          <Select
                            value={item.severity}
                            onValueChange={(value) => updateDiagnosisItem(index, 'severity', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {SEVERITIES.map((sev) => (
                                <SelectItem key={sev.value} value={sev.value}>
                                  {sev.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="mt-3 space-y-2">
                          <Label className="font-medium text-sm">Descripcion del Problema</Label>
                          <Textarea
                            value={item.description}
                            onChange={(e) => updateDiagnosisItem(index, 'description', e.target.value)}
                            rows={2}
                            placeholder="Describa el problema encontrado..."
                            className="px-3 py-2 rounded-xl border border-input bg-background"
                          />
                        </div>

                        <div className="mt-3 space-y-2">
                          <Label className="font-medium text-sm">Recomendacion</Label>
                          <Textarea
                            value={item.recommendation || ''}
                            onChange={(e) => updateDiagnosisItem(index, 'recommendation', e.target.value)}
                            rows={2}
                            placeholder="Recomendacion de reparacion o mantenimiento..."
                            className="px-3 py-2 rounded-xl border border-input bg-background"
                          />
                        </div>

                        {/* Seccion de Imagenes */}
                        <div className="mt-4">
                          <label className="block text-sm font-medium mb-2">Imagenes (Opcional)</label>

                          {/* Boton de carga */}
                          <div className="mb-3 flex flex-wrap items-center gap-2">
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              id={`image-upload-${index}`}
                              onChange={(e) => {
                                handleFilesSelected(index, e.target.files);
                                e.target.value = ''; // Reset input
                              }}
                              className="hidden"
                            />
                            <label
                              htmlFor={`image-upload-${index}`}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-muted cursor-pointer transition-colors text-muted-foreground hover:text-foreground hover:border-foreground/20 hover:bg-muted/50"
                            >
                              <PhotoIcon className="h-5 w-5" />
                              <span className="text-sm">Agregar imagen</span>
                            </label>
                            <button
                              type="button"
                              onClick={() => openPhotoQR(index)}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-muted cursor-pointer transition-colors text-muted-foreground hover:text-foreground hover:border-foreground/20 hover:bg-muted/50"
                            >
                              <QrCodeIcon className="h-5 w-5" />
                              <span className="text-sm">Desde el celular</span>
                            </button>
                          </div>

                          {/* Galeria de imagenes */}
                          {item.images && item.images.length > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              {item.images.map((imagePath, imgIndex) => (
                                <div
                                  key={imgIndex}
                                  className="relative rounded-lg overflow-hidden border border-border"
                                >
                                  <img
                                    src={imagePath}
                                    alt={`Imagen ${imgIndex + 1}`}
                                    className="w-full h-32 object-cover"
                                  />
                                  <Tip label="Eliminar imagen">
                                    <button
                                      type="button"
                                      onClick={() => removeImage(index, imgIndex)}
                                      className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                                    >
                                      <XMarkIcon className="h-4 w-4" />
                                    </button>
                                  </Tip>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </form>
          </div>
          <DialogFooter className="gap-2 px-6 pb-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowEditModal(false);
                clearForm();
              }}
              size="sm"
              className="h-8"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              form="diagnosis-form"
              size="sm"
              className="h-8"
            >
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Vista Previa */}
      <Dialog open={showViewModal} onOpenChange={(open) => {
        if (!open) {
          setShowViewModal(false);
          setViewDiagnosis(null);
          setViewItems([]);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between pr-6">
              <div>
                <DialogTitle className="text-xl font-semibold">Diagnostico #{viewDiagnosis?.diagnosisNumber}</DialogTitle>
                <DialogDescription className="text-sm mt-1">
                  {viewDiagnosis && new Date(viewDiagnosis.createdAt).toLocaleDateString('es-AR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                  })}
                </DialogDescription>
              </div>
              <Button
                onClick={() => viewDiagnosis && handleViewPDF(viewDiagnosis)}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <DocumentTextIcon className="h-4 w-4" />
                PDF
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-6">
            {/* Informacion del Cliente y Vehículo */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm">Informacion del Vehículo</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Cliente:</span>
                  <p className="font-medium">{viewDiagnosis?.clientName}</p>
                </div>
                {viewDiagnosis?.vehiclePlate && (
                  <div>
                    <span className="text-muted-foreground">Vehículo:</span>
                    <p className="font-medium">
                      {viewDiagnosis.vehiclePlate} - {viewDiagnosis.vehicleBrand} {viewDiagnosis.vehicleModel}{' '}
                      {viewDiagnosis.vehicleYear && `(${viewDiagnosis.vehicleYear})`}
                    </p>
                  </div>
                )}
                {viewDiagnosis?.mileage && (
                  <div>
                    <span className="text-muted-foreground">Kilometraje:</span>
                    <p className="font-medium">{viewDiagnosis.mileage.toLocaleString()} km</p>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground">Estado:</span>
                  <div className="mt-1">{viewDiagnosis && getStatusBadge(viewDiagnosis.status)}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Urgencia:</span>
                  <div className="mt-1">{viewDiagnosis && getUrgencyBadge(viewDiagnosis.urgency)}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Dias en estado:</span>
                  <p className="font-medium">{viewDiagnosis ? getStatusDaysLabel(viewDiagnosis) : '-'}</p>
                </div>
              </div>
            </div>

            {/* Incidencias Detectadas */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm">Incidencias Detectadas</h4>
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-muted/50 border-b">
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left w-[180px]">Categoria</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left">Descripcion</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left w-[120px]">Severidad</TableHead>
                      <TableHead className="text-foreground h-10 px-2 align-middle font-medium whitespace-nowrap text-left w-[80px]">Fotos</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {viewItems.map((item, index) => (
                      <TableRow key={item.id || index} className="border-b hover:bg-muted/50">
                        <TableCell className="p-2 align-middle text-left">
                          <div className="flex flex-col gap-1">
                            <span className="font-medium text-sm">{item.category}</span>
                            {item.subcategory && (
                              <span className="text-xs text-muted-foreground">{item.subcategory}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="p-2 align-middle text-left">
                          <div className="space-y-1">
                            {item.description && (
                              <p className="text-sm">{item.description}</p>
                            )}
                            {item.recommendation && (
                              <div className="text-xs bg-muted px-2 py-1 rounded">
                                <span className="font-medium">Recomendacion:</span> {item.recommendation}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="p-2 align-middle text-left">
                          <Badge
                            variant={
                              item.severity === 'critica' || item.severity === 'alta' ? 'destructive' :
                              item.severity === 'media' ? 'default' : 'secondary'
                            }
                            className="text-xs"
                          >
                            {item.severity.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell className="p-2 align-middle text-left">
                          {item.images && item.images.length > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const images = item.images || [];
                                const imageWindow = window.open('', '_blank');
                                if (imageWindow) {
                                  imageWindow.document.write(`
                                    <html>
                                      <head>
                                        <title>Fotos - ${item.category} ${item.subcategory || ''}</title>
                                        <style>
                                          body { margin: 0; padding: 20px; background: #000; display: flex; flex-wrap: wrap; gap: 10px; }
                                          img { max-width: 100%; height: auto; border: 2px solid #fff; }
                                        </style>
                                      </head>
                                      <body>
                                        ${images.map(img => `<img src="${img}" />`).join('')}
                                      </body>
                                    </html>
                                  `);
                                }
                              }}
                              className="h-7 px-2 text-xs"
                            >
                              <PhotoIcon className="h-3.5 w-3.5 mr-1" />
                              {item.images.length}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Observaciones */}
            {viewDiagnosis?.observations && (
              <div className="space-y-2">
                <h4 className="font-semibold text-sm">Observaciones Generales</h4>
                <div className="text-sm text-muted-foreground bg-muted p-3 rounded">
                  {viewDiagnosis.observations}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowViewModal(false);
                setViewDiagnosis(null);
                setViewItems([]);
              }}
            >
              Cerrar
            </Button>
            <Button
              onClick={() => viewDiagnosis && handleEdit(viewDiagnosis)}
            >
              Editar
            </Button>
          </DialogFooter>
        </DialogContent>
        </Dialog>

        {photoQrDialog}
        {vehicleCreateDialog}
      </div>
    </div>
  );
};

export default Diagnostics;





